import React, { Fragment } from 'react';
import { CoordinationState, CoordinationSummary, SolutionState } from 'app/model/SOL49';
import { Button, Col, Divider, Popover, Row, Tag } from 'antd';
import { ForwardOutlined, MessageFilled, StarFilled } from '@ant-design/icons';
import { Flex } from 'app/components/common/Flex';
import ConditionalRender from 'app/common/components/ConditionalRender';
import './CoordinationSummariesPanel.less';
import TimeUtils from 'app/common/util/TimeUtils';
import { CoordinationUtils, SolutionsStatusMap } from 'app/common/util/CoordinationUtils';
import { ModalPanelProps } from 'app/components/panels/DashboardModal';
import Utils from 'app/common/util/Utils';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { CoordinationStatusType } from '../../../model/client/SOL49';

interface CoordinationSummariesPanelProps extends ModalPanelProps {
    coordinationSummaries: CoordinationSummary[];
    statusCache: { [id: string]: SolutionsStatusMap };
    onOpenCoordination: (coordination: CoordinationSummary) => void;
    unreadMessages: { [id: string]: boolean };
}

export class CoordinationSummariesPanel extends React.Component<CoordinationSummariesPanelProps> {
    onOpenCoordination = (coordination: CoordinationSummary) => {
        this.props.onOpenCoordination(coordination);
        this.props.onClose();
        CoordinationUtils.onOpenCoordination(coordination, this.props.onOpenCoordination);
    };

    renderTFVs = (coordination: CoordinationSummary) => {
        const fmps = Object.keys(coordination.status);
        const trafficVolumes = Utils.removeDuplicates(coordination.solutions.flatMap(s => s.tvs));

        return (
            <>
                {trafficVolumes.map((tv, idx) => (
                    <div key={tv}>
                        <ConditionalRender condition={idx !== 0}>
                            <Divider type="horizontal" />
                        </ConditionalRender>
                        <span>{tv}</span>
                    </div>
                ))}
                <ConditionalRender condition={Utils.size(fmps) === 1}>
                    <span className="fmp">{fmps[0]}</span>
                </ConditionalRender>
            </>
        );
    };

    private renderSolutions = (coordination: CoordinationSummary) => {
        return (
            <Flex direction="vertical">
                {coordination.solutions.map((s, idx) => {
                    const solState = coordination.status[AuthorisationUtils.getFmpUnit()]?.[s.name];
                    const needToAnswer =
                        solState && (solState === SolutionState.ACKNOWLEDGED || solState === SolutionState.NO_RESPONSE);
                    return (
                        <div key={s.name}>
                            <Flex>
                                <ConditionalRender condition={needToAnswer}>
                                    <StarFilled style={{ paddingRight: '0.2em' }} />
                                </ConditionalRender>
                                <span>{s.name}</span>
                            </Flex>
                            <ConditionalRender condition={idx < coordination.solutions.length - 1}>
                                <Divider type="horizontal" />
                            </ConditionalRender>
                        </div>
                    );
                })}
            </Flex>
        );
    };

    private renderStatusForState = (state: SolutionState, nbFmps: string[], isUpdate: boolean, showFmps: boolean) => {
        const statusClassname = `status ${nbFmps.length === 0 ? 'inactive' : ''} ${isUpdate ? 'highlight' : ''}`;
        return (
            <Fragment key={state}>
                <Flex direction="vertical" className={statusClassname}>
                    <span>{CoordinationUtils.toSolutionStateShortString(state)}</span>
                    <ConditionalRender condition={showFmps && nbFmps.length > 0}>
                        <span
                            style={{
                                lineHeight: '1.5em',
                                fontWeight: 'normal',
                            }}>{`(${nbFmps.length > 1 ? nbFmps.length : nbFmps[0]})`}</span>
                    </ConditionalRender>
                </Flex>
                <div className="divider-container" style={{ padding: '0 0.5em' }}>
                    <Divider type="vertical" className="divider" />
                </div>
            </Fragment>
        );
    };

    private isUpdate = (currentFmps: string[], previousFmps: string[]) => {
        // Don't highlight updates made by current fmp
        currentFmps = currentFmps.filter(fmp => fmp !== AuthorisationUtils.getFmpUnit());

        return currentFmps.length >= previousFmps.length && Utils.isNotShallowEquals(currentFmps, previousFmps);
    };

    private statusPopover = (status: CoordinationStatusType) => {
        const solutionsNames: string[] = Object.keys(Object.values(status)[0]);
        const fmpList = Object.keys(status);
        return (
            <Flex direction="vertical" grow={false} className="overflow status-popover">
                <Row wrap={false}>
                    <Col span={5}></Col>
                    <Col span={1} className="divider-container">
                        <Divider type="vertical" className="divider" />
                    </Col>
                    {solutionsNames.map((solName, idx) => {
                        return (
                            <Fragment key={solName}>
                                <ConditionalRender condition={idx !== 0}>
                                    <Col span={1} className="divider-container">
                                        <Divider type="vertical" className="divider" />
                                    </Col>
                                </ConditionalRender>
                                <Col span={8}>{solName}</Col>
                            </Fragment>
                        );
                    })}
                </Row>
                <Divider className="horizontal-divider"></Divider>
                {fmpList.map((fmp, i) => {
                    return (
                        <Fragment key={fmp + i}>
                            <Row wrap={false}>
                                <Col span={5} className="fmp">
                                    {fmp}
                                </Col>
                                <Col span={1} className="divider-container">
                                    <Divider type="vertical" className="divider" />
                                </Col>
                                {solutionsNames.map((sol, idx) => (
                                    <Fragment key={sol + idx}>
                                        <ConditionalRender condition={idx !== 0}>
                                            <Col span={1} className="divider-container">
                                                <Divider type="vertical" className="divider" />
                                            </Col>
                                        </ConditionalRender>
                                        <Col span={8} className="solution-state">
                                            <Tag closable={false}>
                                                {CoordinationUtils.toSolutionStateShortString(status[fmp][sol])}
                                            </Tag>
                                        </Col>
                                    </Fragment>
                                ))}
                            </Row>
                            <Divider className="horizontal-divider"></Divider>
                        </Fragment>
                    );
                })}
            </Flex>
        );
    };

    private renderStatus = (coordination: CoordinationSummary) => {
        const concernedFmps = Object.keys(coordination.status);
        return (
            <Popover content={this.statusPopover(coordination.status)} placement={'bottom'}>
                <Flex>
                    <Flex direction="vertical">
                        {coordination.solutions.map((s, solIdx) => {
                            const cache = this.props.statusCache[coordination.id];
                            const current = CoordinationUtils.getCoordinationStatusMap(coordination)[s.name];
                            return (
                                <div key={s.name}>
                                    <ConditionalRender condition={solIdx !== 0}>
                                        <Divider type="horizontal" />
                                    </ConditionalRender>
                                    <Flex>
                                        {Object.values(SolutionState)
                                            .filter(
                                                state =>
                                                    state !== SolutionState.NO_RESPONSE &&
                                                    state !== SolutionState.TIMED_OUT,
                                            )
                                            .map(state => {
                                                const previousFmps = (cache && cache[s.name][state]) || [];
                                                return this.renderStatusForState(
                                                    state,
                                                    current[state],
                                                    coordination.state === CoordinationState.ONGOING &&
                                                        this.isUpdate(current[state], previousFmps),
                                                    Utils.size(concernedFmps) > 1,
                                                );
                                            })}
                                    </Flex>
                                </div>
                            );
                        })}
                    </Flex>
                    <MessageFilled
                        className={`message-icon ${this.props.unreadMessages[coordination.id] ? 'highlight' : ''}`}
                    />
                </Flex>
            </Popover>
        );
    };

    render() {
        const coordinationSummaries = this.props.coordinationSummaries;
        return (
            <div className="coordination-summaries overflow">
                <Row className="header">
                    <Col flex="0 0 4em">Origin</Col>
                    <Col flex="0 0 7em">TFVs</Col>
                    <Col flex="0 0 9em">Solutions</Col>
                    <Col flex="0 0 20em">Status</Col>
                    <Col flex="0 0 2em" style={{ textAlign: 'center' }}>
                        Response Time
                    </Col>
                    <Col flex="0" />
                </Row>
                {coordinationSummaries.map(summary => {
                    const state = summary.state === CoordinationState.ONGOING ? 'OPEN' : summary.state;
                    const cache = this.props.statusCache[summary.id];
                    const hasDiffs = summary.solutions.some(s => {
                        const current = CoordinationUtils.getCoordinationStatusMap(summary)[s.name];
                        return Object.values(SolutionState)
                            .filter(state => state !== SolutionState.NO_RESPONSE && state !== SolutionState.TIMED_OUT)
                            .some(state => {
                                const previousFmps = (cache && cache[s.name][state]) || [];
                                return this.isUpdate(current[state], previousFmps);
                            });
                    });
                    const isUpdate =
                        (summary.state === CoordinationState.ONGOING && hasDiffs) ||
                        this.props.unreadMessages[summary.id];

                    return (
                        <div key={summary.id}>
                            <Row
                                className={`content ${isUpdate ? 'updated' : ''}`}
                                onClick={() => this.onOpenCoordination(summary)}>
                                <Col flex="0 0 2.5em">{CoordinationUtils.getCoordinationOrigin(summary)}</Col>
                                <div className="divider-container">
                                    <Divider type="vertical" className="divider" />
                                </div>
                                <Col flex="0 0 5em">{this.renderTFVs(summary)}</Col>
                                <div className="divider-container">
                                    <Divider type="vertical" className="divider" />
                                </div>
                                <Col flex="0 0 9em" style={{ wordBreak: 'break-all' }}>
                                    {this.renderSolutions(summary)}
                                </Col>
                                <div className="divider-container">
                                    <Divider type="vertical" className="divider" />
                                </div>
                                <Col flex="0 0 19em">{this.renderStatus(summary)}</Col>
                                <div className="divider-container">
                                    <Divider type="vertical" className="divider" />
                                </div>
                                <Col flex="0 0 2em" className="target-time">
                                    {TimeUtils.formatHHMM(summary.targetTime)}
                                </Col>
                                <div className="divider-container">
                                    <Divider type="vertical" className="divider" />
                                </div>
                                <Col flex="0 0 6em" style={{ width: '4em' }}>
                                    <Flex direction="vertical">
                                        <Button
                                            className={`status-button ${isUpdate ? 'highlight' : ''}`}
                                            onClick={() => this.onOpenCoordination(summary)}>
                                            {state}
                                            <ForwardOutlined />
                                        </Button>
                                        <span className="owner">{summary.owner}</span>
                                    </Flex>
                                </Col>
                            </Row>
                        </div>
                    );
                })}
            </div>
        );
    }
}
